import { useEffect, useRef, useState } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';

import { TicketSearched } from 'api/ticket';
import { formatImagesAllow } from 'pages/Ticket/Detail/utils';

import ResultModal from './ResultModal';
import getPdfTemplate from '../pdfTemplate/getPdfTemplate';
import useTicketsExportApis from '../hooks/Service/useTicketsExportApis';

type Props = {
  ticketsData: TicketSearched[];
};

const validateFormatImagesAllow = (url: string) => {
  let urlFormat = '';
  formatImagesAllow.forEach((format) => {
    if (url?.toLocaleLowerCase().includes(format)) urlFormat = url;
  });
  return urlFormat;
};

function ExportPDFZipButton({ ticketsData }: Props) {
  const [ticketsIds, setTicketsIds] = useState<any>(null);
  const pdfData = useRef<any[]>([]);
  const ticketIndex = useRef<number>(0);
  const ticketArray = useRef<any[]>([]);
  const [openStatusModal, setOpenStatusModal] = useState<boolean>(false);
  const [isDownloadingData, setIsDownloadingData] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const loadingTicketDataStatus = useRef<string>('Descargando datos...');
  const { t } = useTranslation('global');

  const {
    clientsDetail,
    clientProjectDetail,
    location,
    images,
    supplies,
    closeTicketComents,
    getBase,
    GetComments,
    ticket,
    isTicketDetailLoading,
    locationIsLoading,
    GetCommentsLoading
  } = useTicketsExportApis(ticketsIds);

  // Separate tickets id and send just one ticket id to trigger useTicketsExportApis
  const extractTicketsId = () => {
    const ids = ticketsData.map((ticket: any) => ({
      ticketId: ticket.ticketId
    }));
    ticketArray.current = ids;
    setTicketsIds(ids[0]);
    setOpenStatusModal(true);
    setIsDownloadingData(true);
    loadingTicketDataStatus.current = `Descargando datos del ticket ${
      ids[0].ticketId
    } (${ticketIndex.current + 1} de ${ticketArray.current.length})`;
    ticketIndex.current++;
  };

  useEffect(() => {
    const exportTickets = async () => {
      if (
        !isTicketDetailLoading &&
        !clientsDetail.isLoading &&
        !clientProjectDetail.isLoading &&
        !isTicketDetailLoading &&
        !locationIsLoading &&
        !images.isLoading &&
        !supplies.isLoading &&
        !closeTicketComents.isLoading &&
        !GetCommentsLoading &&
        ticketsIds
      ) {
        await handleExportPDF();
        // If the current index is less than the array of total tickets,
        // send the following id to trigger useTicketsExportApis.
        // Otherwise, export the zip and remove all variables
        if (ticketIndex.current < ticketArray.current.length) {
          setTicketsIds(ticketArray.current[ticketIndex.current]);
          loadingTicketDataStatus.current = `Descargando datos del ticket ${
            ticketArray.current[ticketIndex.current].ticketId
          } (${ticketIndex.current + 1} de ${ticketArray.current.length})`;
          ticketIndex.current++;
        } else {
          exportZip();
          ticketArray.current = [];
          ticketIndex.current = 0;
          setTicketsIds(null);
          pdfData.current = [];
        }
      }
    };

    exportTickets();
  }, [
    isTicketDetailLoading,
    clientsDetail.isLoading,
    clientProjectDetail.isLoading,
    locationIsLoading,
    images.isLoading,
    supplies.isLoading,
    closeTicketComents.isLoading,
    GetCommentsLoading,
    ticketsIds
  ]);

  const handleExportPDF = async () => {
    const pdfImages = images?.data
      ?.map((url: any) => validateFormatImagesAllow(url?.blobStorageUrlPath))
      ?.filter((elem: any) => elem !== '');

    const base64Logo = await getBase();

    // Collect PDF data (docDefinition and filename)
    pdfData.current.push({
      fileName: `${t('ticket.pdf.pdfName')}-${ticket?.ticketId}.pdf`,
      docDefinition: getPdfTemplate(
        {
          clientActive: clientsDetail?.data,
          projectAndService: clientProjectDetail?.data,
          dataIncident: ticket,
          comments: GetComments?.data?.items,
          location,
          componentLocation: [],
          supplies: supplies?.data,
          closedTicketComments: closeTicketComents?.data
        },
        pdfImages,
        images?.data?.map((item) => item?.description),
        base64Logo
      )
    });
  };

  const exportZip = () => {
    if (ticketArray.current.length == pdfData.current.length) {
      const zip = new JSZip();

      // Generate a PDF with pdfMake and add it to the ZIP
      const { pdfMake } = window;
      function addPdfToZip(zip: any, fileName: any, docDefinition: any) {
        return new Promise((resolve) => {
          pdfMake.createPdf(docDefinition).getBlob((blob) => {
            zip.file(fileName, blob);
            resolve(1);
          });
        });
      }
      // Add PDFs to ZIP
      Promise.all(
        pdfData.current.map((pdf) =>
          addPdfToZip(zip, pdf.fileName, pdf.docDefinition)
        )
      )
        .then(() => {
          // Generate ZIP and download it
          zip.generateAsync({ type: 'blob' }).then((content) => {
            saveAs(content, 'Detalle_de_tickets.zip');
            setIsDownloadingData(false);
            setIsSuccess(true);
          });
        })
        .catch((err) => {
          console.error('Error generating PDFs:', err);
        });
    } else {
      setIsDownloadingData(false);
      setIsSuccess(false);
    }
  };

  const handleClose: DialogProps['onClose'] = (_, reason) => {
    if (reason && reason === 'backdropClick') return;
    setOpenStatusModal(false);
  };

  return (
    <>
      <ResultModal
        acceptChanges={handleClose}
        isLoading={isDownloadingData}
        onClose={handleClose}
        open={openStatusModal}
        statusDescription={loadingTicketDataStatus.current}
        success={isSuccess}
      />

      <Button
        disabled={!ticketsData?.length}
        onClick={extractTicketsId}
        variant="contained"
      >
        {t('ticket.properties.downloadPDF')}
      </Button>
    </>
  );
}

export default ExportPDFZipButton;
